import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useEnterpriseScope } from '@/hooks/useEnterpriseScope';
import { useFlooScopeOptions } from '@/hooks/useFlooScopeOptions';
import { ScopeSwitcher } from '@/components/floo-network/ScopeSwitcher';
import { BackToOptiNetworkButton } from '@/components/floo-network/BackToOptiNetworkButton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import {
  Building2, Map as MapIcon, Layers, Store, Info, Network,
  Plus, Pencil, Archive, ArchiveRestore, MoveRight, Unlink,
  Users, UserPlus, UserMinus, ExternalLink,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Floo Network Phase 4 — Read-only map (Phase 3) PLUS secure hierarchy
// builder controls. Every mutation routes through the
// `manage-floo-network` edge function; server-side authorization is the
// security boundary. UI-side role gating is purely an affordance filter.
//
// What this page shows / does:
//   • Active tree: organization → region → district → shop, archived
//     rows filtered out by default.
//   • Optional "Show archived" toggle per org card surfaces archived
//     regions/districts with Unarchive actions.
//   • OWNER / ORG_ADMIN of an org sees Add / Rename / Archive controls
//     plus Assign / Unassign actions.
//   • Everyone else sees the read-only Phase 3 tree.
//
// Phase 5 additions:
//   • "People" section per org card listing non-shop-scoped memberships.
//   • Assign / Remove member-to-scope dialog (OWNER / ORG_ADMIN only),
//     routed through `manage-floo-network-members`.
//
// What this page does NOT do (deferred to later phases):
//   • Create / rename / archive organizations.
//   • Hard delete anything.
//   • Scope-aware invitations (deferred — invitations remain shop-scoped).
//   • Switch the active scope (Phase 6).
//   • Render region/district dashboards (Phase 7).

type Org = { id: string; name: string; created_at: string };
type Region = {
  id: string;
  name: string;
  organization_id: string;
  archived_at: string | null;
};
type District = {
  id: string;
  name: string;
  organization_id: string;
  region_id: string | null;
  archived_at: string | null;
};
type ShopLite = {
  id: string;
  name: string;
  organization_id: string;
  region_id: string | null;
  district_id: string | null;
  shop_status: string;
};

type OrgMembership = {
  id: string;
  user_id: string;
  role: string;
  scope_type: string;
  scope_id: string | null;
  organization_id: string | null;
  shop_id: string;
  status: string;
  email: string | null;
  display_name: string | null;
};

type BuilderAction =
  | { kind: 'create_region'; organizationId: string }
  | { kind: 'rename_region'; region: Region }
  | { kind: 'create_district'; organizationId: string; defaultRegionId: string | null }
  | { kind: 'rename_district'; district: District }
  | { kind: 'assign_district_to_region'; district: District; orgRegions: Region[] }
  | { kind: 'assign_shop_to_district'; shop: ShopLite; orgDistricts: District[] }
  | {
      kind: 'assign_member_to_scope';
      organizationId: string;
      orgRegions: Region[];
      orgDistricts: District[];
      orgShops: ShopLite[];
      members: OrgMembership[];
      preselectedMembershipId?: string;
    };

// Phase 5 — destination role labels for the UI. The internal enum
// values stay SCREAMING_SNAKE; we map to human labels at the boundary.
const ROLE_LABEL: Record<string, string> = {
  OWNER: 'Owner',
  ORG_ADMIN: 'Organization Admin',
  REGION_MANAGER: 'Region Manager',
  DISTRICT_MANAGER: 'District Manager',
  MANAGER: 'Shop Manager',
  SERVICE_WRITER: 'Service Writer',
  DISPATCHER: 'Dispatcher',
  TECHNICIAN: 'Technician',
  PARTS: 'Parts',
  AUDITOR: 'Auditor',
};

// Roles that may be assigned to a non-shop scope. Operational shop
// roles (SERVICE_WRITER, DISPATCHER, TECHNICIAN, PARTS) are not
// meaningful at org/region/district scope.
const NON_SHOP_ASSIGNABLE_ROLES = [
  'OWNER',
  'ORG_ADMIN',
  'REGION_MANAGER',
  'DISTRICT_MANAGER',
  'MANAGER',
  'AUDITOR',
];

const ALL_ASSIGNABLE_ROLES = [
  'OWNER',
  'ORG_ADMIN',
  'REGION_MANAGER',
  'DISTRICT_MANAGER',
  'MANAGER',
  'SERVICE_WRITER',
  'DISPATCHER',
  'TECHNICIAN',
  'PARTS',
  'AUDITOR',
];

const FlooNetworkMap = () => {
  const { userShops, isShopDataLoading, membership } = useAuth();
  const { canViewOrgDashboard, scopeType } = useEnterpriseScope();
  const queryClient = useQueryClient();
  // Phase 6 — active view scope drives which subtree of the map is
  // focused. The hook handles boundary + persistence + fallback; the
  // page only consumes `activeScope` for filtering.
  const { activeScope: activeViewScope } = useFlooScopeOptions();

  const orgIds = useMemo(() => {
    const ids = new Set<string>();
    for (const s of userShops) {
      if (s.organization_id) ids.add(s.organization_id);
    }
    return Array.from(ids).sort();
  }, [userShops]);
  const orgIdsKey = orgIds.join(',');

  // Phase 4 — UI-side role check. Server-side `manage-floo-network` is
  // the security boundary and re-checks against the target org. We only
  // surface controls when the local role hint says they will succeed.
  const canAdminAnyOrg =
    membership?.role === 'OWNER' || membership?.role === 'ORG_ADMIN';

  const orgsQuery = useQuery({
    queryKey: ['floo-network-map', 'organizations', orgIdsKey],
    enabled: orgIds.length > 0,
    queryFn: async (): Promise<Org[]> => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id,name,created_at')
        .in('id', orgIds)
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as Org[];
    },
  });

  const regionsQuery = useQuery({
    queryKey: ['floo-network-map', 'regions', orgIdsKey],
    enabled: orgIds.length > 0,
    queryFn: async (): Promise<Region[]> => {
      const { data, error } = await supabase
        .from('regions')
        .select('id,name,organization_id,archived_at')
        .in('organization_id', orgIds)
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as Region[];
    },
  });

  const districtsQuery = useQuery({
    queryKey: ['floo-network-map', 'districts', orgIdsKey],
    enabled: orgIds.length > 0,
    queryFn: async (): Promise<District[]> => {
      const { data, error } = await supabase
        .from('districts')
        .select('id,name,organization_id,region_id,archived_at')
        .in('organization_id', orgIds)
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as District[];
    },
  });

  // Phase 5 — org-level memberships, fetched only when the actor can
  // admin some org (the edge function would 403 otherwise). Read goes
  // through `manage-floo-network-members.list_org_memberships` so the
  // service-role join with `profiles` for display names happens
  // server-side; the UI never queries memberships directly.
  const membersQuery = useQuery({
    queryKey: ['floo-network-map', 'memberships', orgIdsKey],
    enabled: orgIds.length > 0 && canAdminAnyOrg,
    queryFn: async (): Promise<Record<string, OrgMembership[]>> => {
      const byOrg: Record<string, OrgMembership[]> = {};
      for (const id of orgIds) {
        const { data, error } = await supabase.functions.invoke(
          'manage-floo-network-members',
          { body: { action: 'list_org_memberships', organization_id: id } },
        );
        if (error) {
          // Skip orgs we lack admin for; surface other errors.
          if ((error as { status?: number })?.status === 403) {
            byOrg[id] = [];
            continue;
          }
          throw error;
        }
        if (data?.ok === false) {
          byOrg[id] = [];
          continue;
        }
        byOrg[id] = (data?.data?.memberships ?? []) as OrgMembership[];
      }
      return byOrg;
    },
  });

  const shops: ShopLite[] = useMemo(
    () =>
      userShops.map((s) => ({
        id: s.id,
        name: s.name,
        organization_id: s.organization_id,
        region_id: s.region_id ?? null,
        district_id: s.district_id ?? null,
        shop_status: s.shop_status,
      })),
    [userShops],
  );

  const orgs = orgsQuery.data ?? [];
  const regions = regionsQuery.data ?? [];
  const districts = districtsQuery.data ?? [];

  const loading =
    isShopDataLoading ||
    orgsQuery.isLoading ||
    regionsQuery.isLoading ||
    districtsQuery.isLoading;

  const loadError =
    orgsQuery.error ?? regionsQuery.error ?? districtsQuery.error ?? null;

  // ── Mutation: single entry point against manage-floo-network ─────────
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['floo-network-map'] });
  };

  const mutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const { data, error } = await supabase.functions.invoke(
        'manage-floo-network',
        { body: payload },
      );
      if (error) throw error;
      if (data && typeof data === 'object' && 'ok' in data && data.ok === false) {
        const message =
          (data as { error?: string }).error ?? 'Hierarchy update failed';
        throw new Error(message);
      }
      return data;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'OptiNetwork updated' });
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error ? err.message : 'Hierarchy update failed';
      toast({ title: 'OptiNetwork update failed', description: message, variant: 'destructive' });
    },
  });

  // Phase 5 — separate mutation channel for membership / scope writes.
  // Routes through `manage-floo-network-members` so the security
  // boundary stays inside one purpose-built edge function and audit
  // entries carry the MEMBERSHIP_SCOPE_* verbs.
  const memberMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const { data, error } = await supabase.functions.invoke(
        'manage-floo-network-members',
        { body: payload },
      );
      if (error) throw error;
      if (data && typeof data === 'object' && 'ok' in data && data.ok === false) {
        const message =
          (data as { error?: string }).error ?? 'People update failed';
        throw new Error(message);
      }
      return data;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'People & scope updated' });
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error ? err.message : 'People update failed';
      toast({ title: 'People update failed', description: message, variant: 'destructive' });
    },
  });

  // ── Builder dialog state ─────────────────────────────────────────────
  const [openAction, setOpenAction] = useState<BuilderAction | null>(null);
  const closeAction = () => setOpenAction(null);

  // Per-org "show archived" toggle.
  const [showArchivedByOrg, setShowArchivedByOrg] = useState<Record<string, boolean>>({});
  const toggleArchived = (orgId: string) =>
    setShowArchivedByOrg((prev) => ({ ...prev, [orgId]: !prev[orgId] }));

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <div className="container mx-auto py-6 px-4 max-w-5xl">
      <div className="mb-2">
        <BackToOptiNetworkButton testId="opti-network-map-back" />
      </div>
      <header className="flex items-start gap-3 mb-2">
        <div className="rounded-md bg-primary/10 p-2 text-primary">
          <Network className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">OptiNetwork Map</h1>
          <p className="text-sm text-muted-foreground">
            View and (for owners and organization admins) build the
            organization → region → district → shop hierarchy visible to your
            account.
          </p>
        </div>
        {/* Phase 6 — page-level switcher (secondary location per spec).
            Component handles its own visibility — hidden when there's no
            switch to make. */}
        <ScopeSwitcher variant="desktop" />
      </header>

      {activeViewScope && (
        <p
          className="text-xs text-muted-foreground mb-4"
          data-testid="floo-active-scope-indicator"
        >
          Viewing <strong>{activeViewScope.levelLabel}</strong> · {activeViewScope.label}
        </p>
      )}

      <Alert className="mb-6">
        <Info className="h-4 w-4" />
        <AlertTitle>Phase 4 — builder is available to owners and organization admins</AlertTitle>
        <AlertDescription>
          Create regions and districts, attach shops, and archive entities
          you no longer use. Hard delete is intentionally not supported —
          archive instead. Manager assignment and scope switching will arrive
          in later OptiNetwork phases. Organization creation remains driven
          by shop onboarding/provisioning today.
        </AlertDescription>
      </Alert>

      {loadError && (
        <Alert variant="destructive" className="mb-6">
          <AlertTitle>Couldn’t load hierarchy data</AlertTitle>
          <AlertDescription>
            One or more hierarchy tables returned an error. This usually means
            the current account does not have read access to those rows. No
            changes have been made.
          </AlertDescription>
        </Alert>
      )}

      {loading && !loadError && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Loading hierarchy…
          </CardContent>
        </Card>
      )}

      {!loading && !loadError && orgIds.length === 0 && (
        <EmptyState
          title="No organizations visible to your account"
          body={
            canViewOrgDashboard
              ? 'Your account has OptiNetwork access, but no organization records were found. This can happen during initial setup before a shop has been provisioned.'
              : 'You may not have permission to view the OptiNetwork hierarchy. Contact an owner or organization admin if you believe this is unexpected.'
          }
        />
      )}

      {!loading && !loadError && orgIds.length > 0 && orgs.length === 0 && (
        <EmptyState
          title="No organizations available"
          body="Your accessible shops point to organization records that could not be read. This usually indicates an RLS or data-integrity gap that requires backend attention."
        />
      )}

      {!loading && !loadError && orgs.length > 0 && (
        <div className="space-y-6">
          {orgs
            // Phase 6 — narrow the orgs list to the org containing the
            // active view scope. For organization-scope view this is a
            // no-op when the user only has one org; for region/district/
            // shop scope it focuses the rendered tree.
            .filter((org) => {
              if (!activeViewScope) return true;
              if (activeViewScope.type === 'organization') return org.id === activeViewScope.id;
              // For sub-org scopes, render the org card if it contains
              // the active scope target.
              if (activeViewScope.type === 'region') {
                return regions.some(
                  (r) => r.id === activeViewScope.id && r.organization_id === org.id,
                );
              }
              if (activeViewScope.type === 'district') {
                return districts.some(
                  (d) => d.id === activeViewScope.id && d.organization_id === org.id,
                );
              }
              if (activeViewScope.type === 'shop') {
                return shops.some(
                  (s) => s.id === activeViewScope.id && s.organization_id === org.id,
                );
              }
              return true;
            })
            .map((org) => {
            // Scope-aware sub-filtering: only include hierarchy rows
            // belonging to the active scope's subtree. The tree
            // renderer continues to handle missing parents/children
            // gracefully (Phase 3 empty-state behavior).
            const allOrgRegions = regions.filter((r) => r.organization_id === org.id);
            const allOrgDistricts = districts.filter((d) => d.organization_id === org.id);
            const allOrgShops = shops.filter((s) => s.organization_id === org.id);

            let orgRegions = allOrgRegions;
            let orgDistricts = allOrgDistricts;
            let orgShops = allOrgShops;

            if (activeViewScope?.type === 'region') {
              orgRegions = allOrgRegions.filter((r) => r.id === activeViewScope.id);
              const regionDistrictIds = new Set(
                allOrgDistricts.filter((d) => d.region_id === activeViewScope.id).map((d) => d.id),
              );
              orgDistricts = allOrgDistricts.filter(
                (d) => d.region_id === activeViewScope.id,
              );
              orgShops = allOrgShops.filter(
                (s) => s.region_id === activeViewScope.id || (s.district_id && regionDistrictIds.has(s.district_id)),
              );
            } else if (activeViewScope?.type === 'district') {
              const district = allOrgDistricts.find((d) => d.id === activeViewScope.id);
              orgRegions = district?.region_id
                ? allOrgRegions.filter((r) => r.id === district.region_id)
                : [];
              orgDistricts = district ? [district] : [];
              orgShops = allOrgShops.filter((s) => s.district_id === activeViewScope.id);
            } else if (activeViewScope?.type === 'shop') {
              const shop = allOrgShops.find((s) => s.id === activeViewScope.id);
              const district = shop?.district_id
                ? allOrgDistricts.find((d) => d.id === shop.district_id)
                : null;
              orgRegions = district?.region_id
                ? allOrgRegions.filter((r) => r.id === district.region_id)
                : shop?.region_id
                  ? allOrgRegions.filter((r) => r.id === shop.region_id)
                  : [];
              orgDistricts = district ? [district] : [];
              orgShops = shop ? [shop] : [];
            }

            const orgMembers = (membersQuery.data ?? {})[org.id] ?? [];
            return (
              <OrgNode
                key={org.id}
                org={org}
                regions={orgRegions}
                districts={orgDistricts}
                shops={orgShops}
                members={orgMembers}
                membersLoading={membersQuery.isLoading}
                canAdmin={canAdminAnyOrg}
                showArchived={!!showArchivedByOrg[org.id]}
                onToggleArchived={() => toggleArchived(org.id)}
                onAction={setOpenAction}
                archiveRegion={(region) =>
                  mutation.mutate({ action: 'archive_region', region_id: region.id })
                }
                unarchiveRegion={(region) =>
                  mutation.mutate({ action: 'unarchive_region', region_id: region.id })
                }
                archiveDistrict={(district) =>
                  mutation.mutate({ action: 'archive_district', district_id: district.id })
                }
                unarchiveDistrict={(district) =>
                  mutation.mutate({ action: 'unarchive_district', district_id: district.id })
                }
                unassignDistrictFromRegion={(district) =>
                  mutation.mutate({
                    action: 'unassign_district_from_region',
                    district_id: district.id,
                  })
                }
                unassignShopFromDistrict={(shop) =>
                  mutation.mutate({
                    action: 'unassign_shop_from_district',
                    shop_id: shop.id,
                  })
                }
                removeMemberFromScope={(m) => {
                  // remove_member_from_scope is full soft-removal
                  // (status='removed'), not just a scope demote. Confirm
                  // before firing — accidental clicks would otherwise
                  // deactivate the member's entire org membership.
                  const label = m.display_name || m.email || m.user_id;
                  const ok = typeof window !== 'undefined'
                    ? window.confirm(
                        `Remove ${label}'s membership? This deactivates their access to ${org.name} entirely. They can be re-invited later via a shop's Team page.`,
                      )
                    : true;
                  if (!ok) return;
                  memberMutation.mutate({
                    action: 'remove_member_from_scope',
                    membership_id: m.id,
                  });
                }}
                mutationPending={mutation.isPending || memberMutation.isPending}
              />
            );
          })}

          {scopeType === 'shop' && (
            <p className="text-xs text-muted-foreground text-center pt-2">
              You are viewing this map at <strong>shop</strong> scope. Higher
              scopes (region / district / organization) will appear once they
              are assigned to your account.
            </p>
          )}
        </div>
      )}

      {openAction && (
        <BuilderDialog
          // Mount a fresh BuilderDialog per action so internal form state
          // is owned by React's mount/unmount lifecycle. Avoids calling
          // setState during render to reset stale form values.
          action={openAction}
          onClose={closeAction}
          onSubmit={(payload) => {
            // Route people/scope writes through the dedicated
            // manage-floo-network-members mutation; hierarchy writes
            // through manage-floo-network.
            const target = openAction.kind === 'assign_member_to_scope'
              ? memberMutation
              : mutation;
            target.mutate(payload, {
              onSuccess: () => closeAction(),
            });
          }}
          pending={mutation.isPending || memberMutation.isPending}
        />
      )}
    </div>
  );
};

// ── Org node ────────────────────────────────────────────────────────────

function OrgNode({
  org,
  regions,
  districts,
  shops,
  members,
  membersLoading,
  canAdmin,
  showArchived,
  onToggleArchived,
  onAction,
  archiveRegion,
  unarchiveRegion,
  archiveDistrict,
  unarchiveDistrict,
  unassignDistrictFromRegion,
  unassignShopFromDistrict,
  removeMemberFromScope,
  mutationPending,
}: {
  org: Org;
  regions: Region[];
  districts: District[];
  shops: ShopLite[];
  members: OrgMembership[];
  membersLoading: boolean;
  canAdmin: boolean;
  showArchived: boolean;
  onToggleArchived: () => void;
  onAction: (a: BuilderAction) => void;
  archiveRegion: (r: Region) => void;
  unarchiveRegion: (r: Region) => void;
  archiveDistrict: (d: District) => void;
  unarchiveDistrict: (d: District) => void;
  unassignDistrictFromRegion: (d: District) => void;
  unassignShopFromDistrict: (s: ShopLite) => void;
  removeMemberFromScope: (m: OrgMembership) => void;
  mutationPending: boolean;
}) {
  const activeRegions = regions.filter((r) => !r.archived_at);
  const archivedRegions = regions.filter((r) => !!r.archived_at);
  const activeDistricts = districts.filter((d) => !d.archived_at);
  const archivedDistricts = districts.filter((d) => !!d.archived_at);

  // Bucket active districts by region for the active tree.
  const districtsByRegion = new Map<string, District[]>();
  const districtsWithoutRegion: District[] = [];
  for (const d of activeDistricts) {
    if (d.region_id && activeRegions.some((r) => r.id === d.region_id)) {
      const list = districtsByRegion.get(d.region_id) ?? [];
      list.push(d);
      districtsByRegion.set(d.region_id, list);
    } else {
      // Either no region_id, or the parent region is archived — surface
      // under "no region" so the district stays addressable.
      districtsWithoutRegion.push(d);
    }
  }

  const shopsByDistrict = new Map<string, ShopLite[]>();
  const shopsByRegionNoDistrict = new Map<string, ShopLite[]>();
  const shopsUnassigned: ShopLite[] = [];
  for (const s of shops) {
    const dId = s.district_id;
    const inActiveDistrict = dId && activeDistricts.some((d) => d.id === dId);
    if (inActiveDistrict) {
      const list = shopsByDistrict.get(dId) ?? [];
      list.push(s);
      shopsByDistrict.set(dId, list);
    } else if (s.region_id && activeRegions.some((r) => r.id === s.region_id)) {
      const list = shopsByRegionNoDistrict.get(s.region_id) ?? [];
      list.push(s);
      shopsByRegionNoDistrict.set(s.region_id, list);
    } else {
      shopsUnassigned.push(s);
    }
  }

  const totalArchived = archivedRegions.length + archivedDistricts.length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 flex-wrap">
          <Building2 className="h-5 w-5 text-primary" />
          {org.name}
          <Badge variant="secondary" className="text-[10px]">
            {activeRegions.length} region{activeRegions.length === 1 ? '' : 's'}
          </Badge>
          <Badge variant="secondary" className="text-[10px]">
            {activeDistricts.length} district{activeDistricts.length === 1 ? '' : 's'}
          </Badge>
          <Badge variant="secondary" className="text-[10px]">
            {shops.length} shop{shops.length === 1 ? '' : 's'}
          </Badge>
          {canAdmin && (
            <div className="ml-auto flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => onAction({ kind: 'create_region', organizationId: org.id })}
                disabled={mutationPending}
              >
                <Plus className="h-3 w-3 mr-1" />
                Region
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  onAction({
                    kind: 'create_district',
                    organizationId: org.id,
                    defaultRegionId: null,
                  })
                }
                disabled={mutationPending}
              >
                <Plus className="h-3 w-3 mr-1" />
                District
              </Button>
            </div>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {activeRegions.length === 0 && districtsWithoutRegion.length === 0 && (
          <InlineEmpty>
            No regions or districts yet for this organization.
            {canAdmin && ' Use the buttons above to add the first region or district.'}
          </InlineEmpty>
        )}

        {activeRegions.map((region) => (
          <RegionNode
            key={region.id}
            region={region}
            districts={districtsByRegion.get(region.id) ?? []}
            shopsByDistrict={shopsByDistrict}
            shopsInRegionNoDistrict={shopsByRegionNoDistrict.get(region.id) ?? []}
            canAdmin={canAdmin}
            mutationPending={mutationPending}
            allActiveRegions={activeRegions}
            allActiveDistricts={activeDistricts}
            onAction={onAction}
            archiveRegion={archiveRegion}
            archiveDistrict={archiveDistrict}
            unassignDistrictFromRegion={unassignDistrictFromRegion}
            unassignShopFromDistrict={unassignShopFromDistrict}
          />
        ))}

        {districtsWithoutRegion.length > 0 && (
          <RegionlessGroup
            districts={districtsWithoutRegion}
            shopsByDistrict={shopsByDistrict}
            canAdmin={canAdmin}
            mutationPending={mutationPending}
            orgRegions={activeRegions}
            onAction={onAction}
            archiveDistrict={archiveDistrict}
            unassignShopFromDistrict={unassignShopFromDistrict}
          />
        )}

        {shopsUnassigned.length > 0 && (
          <UnassignedShops
            shops={shopsUnassigned}
            canAdmin={canAdmin}
            mutationPending={mutationPending}
            orgDistricts={activeDistricts}
            onAction={onAction}
          />
        )}

        <OrgPeopleSection
          org={org}
          orgRegions={activeRegions}
          orgDistricts={activeDistricts}
          orgShops={shops}
          members={members}
          membersLoading={membersLoading}
          canAdmin={canAdmin}
          mutationPending={mutationPending}
          onAction={onAction}
          removeMemberFromScope={removeMemberFromScope}
        />

        {totalArchived > 0 && (
          <div className="pt-2 border-t">
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground underline"
              onClick={onToggleArchived}
            >
              {showArchived ? 'Hide' : 'Show'} archived ({totalArchived})
            </button>
            {showArchived && (
              <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                {archivedRegions.map((r) => (
                  <div key={r.id} className="flex items-center gap-2">
                    <MapIcon className="h-3 w-3" />
                    <span className="italic line-through">{r.name}</span>
                    <span className="text-[10px]">(region)</span>
                    {canAdmin && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-[10px]"
                        onClick={() => unarchiveRegion(r)}
                        disabled={mutationPending}
                      >
                        <ArchiveRestore className="h-3 w-3 mr-1" />
                        Unarchive
                      </Button>
                    )}
                  </div>
                ))}
                {archivedDistricts.map((d) => (
                  <div key={d.id} className="flex items-center gap-2">
                    <Layers className="h-3 w-3" />
                    <span className="italic line-through">{d.name}</span>
                    <span className="text-[10px]">(district)</span>
                    {canAdmin && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-[10px]"
                        onClick={() => unarchiveDistrict(d)}
                        disabled={mutationPending}
                      >
                        <ArchiveRestore className="h-3 w-3 mr-1" />
                        Unarchive
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Region node ─────────────────────────────────────────────────────────

function RegionNode({
  region,
  districts,
  shopsByDistrict,
  shopsInRegionNoDistrict,
  canAdmin,
  mutationPending,
  allActiveRegions,
  allActiveDistricts,
  onAction,
  archiveRegion,
  archiveDistrict,
  unassignDistrictFromRegion,
  unassignShopFromDistrict,
}: {
  region: Region;
  districts: District[];
  shopsByDistrict: Map<string, ShopLite[]>;
  shopsInRegionNoDistrict: ShopLite[];
  canAdmin: boolean;
  mutationPending: boolean;
  allActiveRegions: Region[];
  allActiveDistricts: District[];
  onAction: (a: BuilderAction) => void;
  archiveRegion: (r: Region) => void;
  archiveDistrict: (d: District) => void;
  unassignDistrictFromRegion: (d: District) => void;
  unassignShopFromDistrict: (s: ShopLite) => void;
}) {
  const totalShopsInRegion =
    districts.reduce(
      (sum, d) => sum + (shopsByDistrict.get(d.id)?.length ?? 0),
      0,
    ) + shopsInRegionNoDistrict.length;

  return (
    <section className="border-l-2 border-muted pl-4 ml-2">
      <header className="flex items-center gap-2 flex-wrap mb-2">
        <MapIcon className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">{region.name}</span>
        <Badge variant="outline" className="text-[10px]">
          {districts.length} district{districts.length === 1 ? '' : 's'}
        </Badge>
        <Badge variant="outline" className="text-[10px]">
          {totalShopsInRegion} shop{totalShopsInRegion === 1 ? '' : 's'}
        </Badge>
        {/* Phase 7 — drill-down link to Region Dashboard. Placed
            outside `canAdmin` so any role that can see the map can also
            open the dashboard; dashboard page-level access gate filters
            actual access. */}
        <RegionOpenButton regionId={region.id} className={canAdmin ? '' : 'ml-auto'} />
        {canAdmin && (
          <div className="ml-auto flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2"
              onClick={() => onAction({ kind: 'rename_region', region })}
              disabled={mutationPending}
              title="Rename region"
            >
              <Pencil className="h-3 w-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2"
              onClick={() =>
                onAction({
                  kind: 'create_district',
                  organizationId: region.organization_id,
                  defaultRegionId: region.id,
                })
              }
              disabled={mutationPending}
              title="Add district under this region"
            >
              <Plus className="h-3 w-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2"
              onClick={() => archiveRegion(region)}
              disabled={mutationPending}
              title="Archive region"
            >
              <Archive className="h-3 w-3" />
            </Button>
          </div>
        )}
      </header>

      {districts.length === 0 && shopsInRegionNoDistrict.length === 0 && (
        <InlineEmpty>No districts created in this region yet.</InlineEmpty>
      )}

      <div className="space-y-3">
        {districts.map((district) => (
          <DistrictNode
            key={district.id}
            district={district}
            shops={shopsByDistrict.get(district.id) ?? []}
            canAdmin={canAdmin}
            mutationPending={mutationPending}
            orgRegions={allActiveRegions}
            orgDistricts={allActiveDistricts}
            onAction={onAction}
            archiveDistrict={archiveDistrict}
            unassignDistrictFromRegion={unassignDistrictFromRegion}
            unassignShopFromDistrict={unassignShopFromDistrict}
            isAssignedToARegion
          />
        ))}

        {shopsInRegionNoDistrict.length > 0 && (
          <div className="border-l-2 border-dashed border-muted pl-4 ml-2">
            <header className="flex items-center gap-2 flex-wrap mb-1">
              <Layers className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground italic">
                Shops in this region not yet assigned to a district
              </span>
              <Badge variant="outline" className="text-[10px]">
                {shopsInRegionNoDistrict.length}
              </Badge>
            </header>
            <ul className="space-y-1">
              {shopsInRegionNoDistrict.map((s) => (
                <ShopRow
                  key={s.id}
                  shop={s}
                  canAdmin={canAdmin}
                  mutationPending={mutationPending}
                  orgDistricts={allActiveDistricts}
                  onAction={onAction}
                  unassignShopFromDistrict={unassignShopFromDistrict}
                />
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

// ── District node ───────────────────────────────────────────────────────

function DistrictNode({
  district,
  shops,
  canAdmin,
  mutationPending,
  orgRegions,
  orgDistricts,
  onAction,
  archiveDistrict,
  unassignDistrictFromRegion,
  unassignShopFromDistrict,
  isAssignedToARegion,
}: {
  district: District;
  shops: ShopLite[];
  canAdmin: boolean;
  mutationPending: boolean;
  orgRegions: Region[];
  orgDistricts: District[];
  onAction: (a: BuilderAction) => void;
  archiveDistrict: (d: District) => void;
  unassignDistrictFromRegion: (d: District) => void;
  unassignShopFromDistrict: (s: ShopLite) => void;
  isAssignedToARegion: boolean;
}) {
  return (
    <section className="border-l-2 border-muted pl-4 ml-2">
      <header className="flex items-center gap-2 flex-wrap mb-1">
        <Layers className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">{district.name}</span>
        <Badge variant="outline" className="text-[10px]">
          {shops.length} shop{shops.length === 1 ? '' : 's'}
        </Badge>
        {/* Phase 7 — drill-down link to District Dashboard. */}
        <DistrictOpenButton districtId={district.id} className={canAdmin ? '' : 'ml-auto'} />
        {canAdmin && (
          <div className="ml-auto flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2"
              onClick={() => onAction({ kind: 'rename_district', district })}
              disabled={mutationPending}
              title="Rename district"
            >
              <Pencil className="h-3 w-3" />
            </Button>
            {isAssignedToARegion ? (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2"
                onClick={() => unassignDistrictFromRegion(district)}
                disabled={mutationPending}
                title="Unassign district from region"
              >
                <Unlink className="h-3 w-3" />
              </Button>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2"
                onClick={() =>
                  onAction({
                    kind: 'assign_district_to_region',
                    district,
                    orgRegions,
                  })
                }
                disabled={mutationPending || orgRegions.length === 0}
                title={
                  orgRegions.length === 0
                    ? 'Create a region first'
                    : 'Assign district to a region'
                }
              >
                <MoveRight className="h-3 w-3" />
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2"
              onClick={() => archiveDistrict(district)}
              disabled={mutationPending}
              title="Archive district"
            >
              <Archive className="h-3 w-3" />
            </Button>
          </div>
        )}
      </header>
      {shops.length === 0 ? (
        <InlineEmpty>No shops assigned to this district yet.</InlineEmpty>
      ) : (
        <ul className="space-y-1">
          {shops.map((s) => (
            <ShopRow
              key={s.id}
              shop={s}
              canAdmin={canAdmin}
              mutationPending={mutationPending}
              orgDistricts={orgDistricts}
              onAction={onAction}
              unassignShopFromDistrict={unassignShopFromDistrict}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

// ── Regionless / unassigned ─────────────────────────────────────────────

function RegionlessGroup({
  districts,
  shopsByDistrict,
  canAdmin,
  mutationPending,
  orgRegions,
  onAction,
  archiveDistrict,
  unassignShopFromDistrict,
}: {
  districts: District[];
  shopsByDistrict: Map<string, ShopLite[]>;
  canAdmin: boolean;
  mutationPending: boolean;
  orgRegions: Region[];
  onAction: (a: BuilderAction) => void;
  archiveDistrict: (d: District) => void;
  unassignShopFromDistrict: (s: ShopLite) => void;
}) {
  return (
    <section className="border-l-2 border-dashed border-muted pl-4 ml-2">
      <header className="flex items-center gap-2 flex-wrap mb-2">
        <MapIcon className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground italic">
          Districts not yet assigned to a region
        </span>
        <Badge variant="outline" className="text-[10px]">
          {districts.length}
        </Badge>
      </header>
      <div className="space-y-3">
        {districts.map((d) => (
          <DistrictNode
            key={d.id}
            district={d}
            shops={shopsByDistrict.get(d.id) ?? []}
            canAdmin={canAdmin}
            mutationPending={mutationPending}
            orgRegions={orgRegions}
            orgDistricts={districts}
            onAction={onAction}
            archiveDistrict={archiveDistrict}
            unassignDistrictFromRegion={() => {
              /* not reachable here — district has no region */
            }}
            unassignShopFromDistrict={unassignShopFromDistrict}
            isAssignedToARegion={false}
          />
        ))}
      </div>
    </section>
  );
}

function UnassignedShops({
  shops,
  canAdmin,
  mutationPending,
  orgDistricts,
  onAction,
}: {
  shops: ShopLite[];
  canAdmin: boolean;
  mutationPending: boolean;
  orgDistricts: District[];
  onAction: (a: BuilderAction) => void;
}) {
  return (
    <section className="border-l-2 border-dashed border-muted pl-4 ml-2">
      <header className="flex items-center gap-2 flex-wrap mb-1">
        <Store className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground italic">
          Shops not yet assigned to a region or district
        </span>
        <Badge variant="outline" className="text-[10px]">
          {shops.length}
        </Badge>
      </header>
      <ul className="space-y-1">
        {shops.map((s) => (
          <ShopRow
            key={s.id}
            shop={s}
            canAdmin={canAdmin}
            mutationPending={mutationPending}
            orgDistricts={orgDistricts}
            onAction={onAction}
            unassignShopFromDistrict={() => {
              /* already unassigned */
            }}
          />
        ))}
      </ul>
    </section>
  );
}

// ── Shop row ────────────────────────────────────────────────────────────

function ShopRow({
  shop,
  canAdmin,
  mutationPending,
  orgDistricts,
  onAction,
  unassignShopFromDistrict,
}: {
  shop: ShopLite;
  canAdmin: boolean;
  mutationPending: boolean;
  orgDistricts: District[];
  onAction: (a: BuilderAction) => void;
  unassignShopFromDistrict: (s: ShopLite) => void;
}) {
  // `shops.shop_status` enum is `'active' | 'canceled' | 'suspended'`;
  // a "closed" shop is stored as `shop_status='canceled'`. Pre-Phase-6
  // this checked `=== 'closed'` (a never-matching value), which meant
  // canceled and suspended shops looked active in the UI. The Phase 6
  // stabilization audit corrected the comparison.
  const isInactive = shop.shop_status !== 'active';
  const statusLabel = shop.shop_status; // 'canceled' | 'suspended'
  return (
    <li className="flex items-center gap-2 text-sm">
      <Store
        className={cn(
          'h-4 w-4',
          isInactive ? 'text-muted-foreground' : 'text-primary',
        )}
      />
      <span className={cn(isInactive && 'line-through text-muted-foreground')}>
        {shop.name}
      </span>
      {isInactive && (
        <Badge variant="outline" className="text-[10px]">
          {statusLabel}
        </Badge>
      )}
      {canAdmin && !isInactive && (
        <div className="ml-auto flex items-center gap-1">
          {shop.district_id ? (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2 text-[10px]"
              onClick={() => unassignShopFromDistrict(shop)}
              disabled={mutationPending}
              title="Unassign shop from district"
            >
              <Unlink className="h-3 w-3" />
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2 text-[10px]"
              onClick={() =>
                onAction({
                  kind: 'assign_shop_to_district',
                  shop,
                  orgDistricts,
                })
              }
              disabled={mutationPending || orgDistricts.length === 0}
              title={
                orgDistricts.length === 0
                  ? 'Create a district first'
                  : 'Assign shop to district'
              }
            >
              <MoveRight className="h-3 w-3" />
            </Button>
          )}
        </div>
      )}
    </li>
  );
}

// ── Builder dialog ──────────────────────────────────────────────────────

function BuilderDialog({
  action,
  onClose,
  onSubmit,
  pending,
}: {
  action: BuilderAction;
  onClose: () => void;
  onSubmit: (payload: Record<string, unknown>) => void;
  pending: boolean;
}) {
  // Initial form state derives directly from the action prop. Because the
  // parent mounts a fresh BuilderDialog per action, useState's lazy initializer
  // runs once per dialog opening and we never need to reset state mid-life.
  const [name, setName] = useState<string>(() =>
    action.kind === 'rename_region' ? action.region.name :
    action.kind === 'rename_district' ? action.district.name : '',
  );
  const [regionId, setRegionId] = useState<string | null>(() =>
    action.kind === 'create_district' ? action.defaultRegionId : null,
  );
  const [districtId, setDistrictId] = useState<string | null>(null);
  const [membershipId, setMembershipId] = useState<string | null>(() =>
    action.kind === 'assign_member_to_scope' ? action.preselectedMembershipId ?? null : null,
  );
  const [memberScopeType, setMemberScopeType] = useState<string | null>(null);
  const [memberScopeId, setMemberScopeId] = useState<string | null>(null);
  const [memberRole, setMemberRole] = useState<string | null>(null);

  const title = (() => {
    switch (action.kind) {
      case 'create_region': return 'Add region';
      case 'rename_region': return 'Rename region';
      case 'create_district': return 'Add district';
      case 'rename_district': return 'Rename district';
      case 'assign_district_to_region': return 'Assign district to region';
      case 'assign_shop_to_district': return 'Assign shop to district';
      case 'assign_member_to_scope': return 'Assign person to a scope';
    }
  })();

  // Pre-compute whether the current form is submittable so we can both
  // disable the button and short-circuit submit().
  const trimmedName = name.trim();
  const isValid = (() => {
    switch (action.kind) {
      case 'create_region':
      case 'rename_region':
      case 'create_district':
      case 'rename_district':
        return trimmedName.length > 0;
      case 'assign_district_to_region':
        return regionId != null && action.orgRegions.length > 0;
      case 'assign_shop_to_district':
        return districtId != null && action.orgDistricts.length > 0;
      case 'assign_member_to_scope':
        if (!membershipId || !memberScopeType || !memberScopeId || !memberRole) return false;
        // Operational shop roles cannot land at non-shop scope.
        if (memberScopeType !== 'shop' && !NON_SHOP_ASSIGNABLE_ROLES.includes(memberRole)) return false;
        return true;
    }
  })();

  const submit = () => {
    if (!isValid) return;
    switch (action.kind) {
      case 'create_region':
        onSubmit({ action: 'create_region', organization_id: action.organizationId, name: trimmedName });
        break;
      case 'rename_region':
        onSubmit({ action: 'rename_region', region_id: action.region.id, name: trimmedName });
        break;
      case 'create_district':
        onSubmit({
          action: 'create_district',
          organization_id: action.organizationId,
          name: trimmedName,
          ...(regionId ? { region_id: regionId } : {}),
        });
        break;
      case 'rename_district':
        onSubmit({ action: 'rename_district', district_id: action.district.id, name: trimmedName });
        break;
      case 'assign_district_to_region':
        onSubmit({
          action: 'assign_district_to_region',
          district_id: action.district.id,
          region_id: regionId,
        });
        break;
      case 'assign_shop_to_district':
        onSubmit({
          action: 'assign_shop_to_district',
          shop_id: action.shop.id,
          district_id: districtId,
        });
        break;
      case 'assign_member_to_scope':
        onSubmit({
          action: 'assign_member_to_scope',
          membership_id: membershipId,
          scope_type: memberScopeType,
          scope_id: memberScopeId,
          role: memberRole,
        });
        break;
    }
  };

  // For the member-assign dialog: when scope type changes, clear the
  // dependent scope_id so we don't carry a stale shop id into a
  // district selection (etc.).
  const onMemberScopeTypeChange = (next: string) => {
    setMemberScopeType(next);
    setMemberScopeId(null);
    // Snap an invalid role back to a sensible default when switching to
    // a non-shop scope: if the previously chosen role is an operational
    // shop role, fall through to the safer "MANAGER" placeholder so
    // the user has to consciously re-pick.
    if (next !== 'shop' && memberRole && !NON_SHOP_ASSIGNABLE_ROLES.includes(memberRole)) {
      setMemberRole(null);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Changes are written through the secure manage-floo-network edge
            function and audit-logged.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {(action.kind === 'create_region' ||
            action.kind === 'rename_region' ||
            action.kind === 'create_district' ||
            action.kind === 'rename_district') && (
            <div className="space-y-1">
              <Label htmlFor="floo-name">Name</Label>
              <Input
                id="floo-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={
                  action.kind === 'create_region' ? 'e.g. Midwest Region' :
                  action.kind === 'create_district' ? 'e.g. Kansas City District' :
                  'New name'
                }
                maxLength={120}
                autoFocus
              />
            </div>
          )}

          {action.kind === 'create_district' && (
            <div className="space-y-1">
              <Label htmlFor="floo-region">Region (optional)</Label>
              <p className="text-xs text-muted-foreground">
                Leave unset to create the district without a region; you can assign one later.
              </p>
              {/* Region picker uses the parent map's region data, which is
                  passed through the action payload upstream — but for
                  create_district we don't pre-load regions in the action
                  payload, so the picker is omitted for now and the user
                  can attach the district via the Assign action after
                  creation. */}
              <p className="text-xs italic text-muted-foreground">
                {action.defaultRegionId
                  ? 'This district will be created inside the selected region.'
                  : 'You can attach this district to a region after creating it.'}
              </p>
            </div>
          )}

          {action.kind === 'assign_district_to_region' && (
            <div className="space-y-1">
              <Label htmlFor="floo-region-select">Region</Label>
              {action.orgRegions.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No active regions in this organization yet. Create one first.
                </p>
              ) : (
                <Select
                  value={regionId ?? undefined}
                  onValueChange={(v) => setRegionId(v)}
                >
                  <SelectTrigger id="floo-region-select">
                    <SelectValue placeholder="Select a region" />
                  </SelectTrigger>
                  <SelectContent>
                    {action.orgRegions.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {action.kind === 'assign_shop_to_district' && (
            <div className="space-y-1">
              <Label htmlFor="floo-district-select">District</Label>
              {action.orgDistricts.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No active districts in this organization yet. Create one first.
                </p>
              ) : (
                <Select
                  value={districtId ?? undefined}
                  onValueChange={(v) => setDistrictId(v)}
                >
                  <SelectTrigger id="floo-district-select">
                    <SelectValue placeholder="Select a district" />
                  </SelectTrigger>
                  <SelectContent>
                    {action.orgDistricts.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {action.kind === 'assign_member_to_scope' && (
            <>
              <p className="text-xs text-muted-foreground italic">
                People assigned to a non-shop scope inherit access to all
                scopes underneath that level. Members must already belong
                to this organization through an existing shop membership;
                scope-aware invitations are deferred to a later phase.
              </p>

              {/* Member picker */}
              <div className="space-y-1">
                <Label htmlFor="floo-member-select">Person</Label>
                {action.members.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No memberships found for this organization. Invite a user
                    via the shop's Team page first, then return here to
                    reassign their scope.
                  </p>
                ) : (
                  <Select
                    value={membershipId ?? undefined}
                    onValueChange={(v) => setMembershipId(v)}
                  >
                    <SelectTrigger id="floo-member-select">
                      <SelectValue placeholder="Select a person" />
                    </SelectTrigger>
                    <SelectContent>
                      {action.members.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.display_name || m.email || m.user_id} — {ROLE_LABEL[m.role] ?? m.role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Scope type picker */}
              <div className="space-y-1">
                <Label htmlFor="floo-member-scope-type">Scope</Label>
                <Select
                  value={memberScopeType ?? undefined}
                  onValueChange={onMemberScopeTypeChange}
                >
                  <SelectTrigger id="floo-member-scope-type">
                    <SelectValue placeholder="Select a scope level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="organization">Organization</SelectItem>
                    <SelectItem value="region" disabled={action.orgRegions.length === 0}>
                      Region {action.orgRegions.length === 0 ? '(none yet)' : ''}
                    </SelectItem>
                    <SelectItem value="district" disabled={action.orgDistricts.length === 0}>
                      District {action.orgDistricts.length === 0 ? '(none yet)' : ''}
                    </SelectItem>
                    <SelectItem value="shop" disabled={action.orgShops.length === 0}>
                      Shop {action.orgShops.length === 0 ? '(none yet)' : ''}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Scope id picker — depends on type */}
              {memberScopeType && (
                <div className="space-y-1">
                  <Label htmlFor="floo-member-scope-id">
                    {memberScopeType === 'organization' ? 'Organization' :
                     memberScopeType === 'region' ? 'Region' :
                     memberScopeType === 'district' ? 'District' : 'Shop'}
                  </Label>
                  {memberScopeType === 'organization' ? (
                    <Select
                      value={memberScopeId ?? undefined}
                      onValueChange={(v) => setMemberScopeId(v)}
                    >
                      <SelectTrigger id="floo-member-scope-id">
                        <SelectValue placeholder="Select organization" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={action.organizationId}>(this organization)</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : memberScopeType === 'region' ? (
                    <Select
                      value={memberScopeId ?? undefined}
                      onValueChange={(v) => setMemberScopeId(v)}
                    >
                      <SelectTrigger id="floo-member-scope-id">
                        <SelectValue placeholder="Select region" />
                      </SelectTrigger>
                      <SelectContent>
                        {action.orgRegions.map((r) => (
                          <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : memberScopeType === 'district' ? (
                    <Select
                      value={memberScopeId ?? undefined}
                      onValueChange={(v) => setMemberScopeId(v)}
                    >
                      <SelectTrigger id="floo-member-scope-id">
                        <SelectValue placeholder="Select district" />
                      </SelectTrigger>
                      <SelectContent>
                        {action.orgDistricts.map((d) => (
                          <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Select
                      value={memberScopeId ?? undefined}
                      onValueChange={(v) => setMemberScopeId(v)}
                    >
                      <SelectTrigger id="floo-member-scope-id">
                        <SelectValue placeholder="Select shop" />
                      </SelectTrigger>
                      <SelectContent>
                        {action.orgShops.map((s) => {
                          // `shop_status` enum is `'active' | 'canceled' |
                          // 'suspended'`. Non-active shops are disabled and
                          // labeled with their actual state (Phase 6
                          // stabilization audit — previous code checked
                          // `=== 'closed'`, a value that never appears).
                          const inactive = s.shop_status !== 'active';
                          return (
                            <SelectItem
                              key={s.id}
                              value={s.id}
                              disabled={inactive}
                            >
                              {s.name}{inactive ? ` (${s.shop_status})` : ''}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {/* Role picker */}
              <div className="space-y-1">
                <Label htmlFor="floo-member-role">Role</Label>
                <Select
                  value={memberRole ?? undefined}
                  onValueChange={(v) => setMemberRole(v)}
                >
                  <SelectTrigger id="floo-member-role">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {(memberScopeType && memberScopeType !== 'shop'
                      ? NON_SHOP_ASSIGNABLE_ROLES
                      : ALL_ASSIGNABLE_ROLES
                    ).map((r) => (
                      <SelectItem key={r} value={r}>{ROLE_LABEL[r] ?? r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {memberScopeType && memberScopeType !== 'shop' && (
                  <p className="text-[10px] italic text-muted-foreground">
                    Service Writer, Dispatcher, Technician, and Parts are only assignable at shop scope.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={pending || !isValid}>
            {pending ? 'Working…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Org people section (Phase 5) ────────────────────────────────────────

function OrgPeopleSection({
  org,
  orgRegions,
  orgDistricts,
  orgShops,
  members,
  membersLoading,
  canAdmin,
  mutationPending,
  onAction,
  removeMemberFromScope,
}: {
  org: Org;
  orgRegions: Region[];
  orgDistricts: District[];
  orgShops: ShopLite[];
  members: OrgMembership[];
  membersLoading: boolean;
  canAdmin: boolean;
  mutationPending: boolean;
  onAction: (a: BuilderAction) => void;
  removeMemberFromScope: (m: OrgMembership) => void;
}) {
  // Surface non-shop-scoped members in the People section. Shop-scoped
  // memberships continue to live under each shop's own Team page; this
  // section is the Floo-Network-level admin view.
  const nonShopMembers = members.filter(
    (m) => m.scope_type === 'organization' || m.scope_type === 'region' || m.scope_type === 'district',
  );

  if (!canAdmin && nonShopMembers.length === 0) {
    // Don't add visual noise for read-only users when there's nothing to show.
    return null;
  }

  return (
    <section className="pt-2 border-t">
      <header className="flex items-center gap-2 flex-wrap mb-2">
        <Users className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium text-sm">People at organization, region, and district scope</span>
        <Badge variant="outline" className="text-[10px]" data-testid="floo-people-count">
          {nonShopMembers.length}
        </Badge>
        {canAdmin && (
          <Button
            size="sm"
            variant="outline"
            className="ml-auto h-7 px-2 gap-1"
            onClick={() =>
              onAction({
                kind: 'assign_member_to_scope',
                organizationId: org.id,
                orgRegions,
                orgDistricts,
                orgShops,
                members,
              })
            }
            disabled={mutationPending || members.length === 0}
            title={members.length === 0 ? 'No memberships in this organization yet' : 'Assign a person to a scope'}
            data-testid="floo-assign-member-button"
          >
            <UserPlus className="h-3 w-3" />
            Assign
          </Button>
        )}
      </header>
      <p className="text-xs text-muted-foreground italic mb-2">
        People assigned here inherit access to the scopes below this level.
        Shop-only memberships are managed from each shop's Team page.
      </p>

      {membersLoading && (
        <InlineEmpty>Loading people…</InlineEmpty>
      )}

      {!membersLoading && nonShopMembers.length === 0 && (
        <InlineEmpty>
          No org / region / district-scoped members yet.
          {canAdmin && ' Use the Assign button above to promote an existing shop member.'}
        </InlineEmpty>
      )}

      {!membersLoading && nonShopMembers.length > 0 && (
        <ul className="space-y-1">
          {nonShopMembers.map((m) => (
            <PersonRow
              key={m.id}
              member={m}
              orgRegions={orgRegions}
              orgDistricts={orgDistricts}
              canAdmin={canAdmin}
              mutationPending={mutationPending}
              removeMemberFromScope={removeMemberFromScope}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function PersonRow({
  member,
  orgRegions,
  orgDistricts,
  canAdmin,
  mutationPending,
  removeMemberFromScope,
}: {
  member: OrgMembership;
  orgRegions: Region[];
  orgDistricts: District[];
  canAdmin: boolean;
  mutationPending: boolean;
  removeMemberFromScope: (m: OrgMembership) => void;
}) {
  const scopeLabel = (() => {
    if (member.scope_type === 'organization') return 'Organization';
    if (member.scope_type === 'region') {
      const r = orgRegions.find((x) => x.id === member.scope_id);
      return r ? `Region · ${r.name}` : 'Region';
    }
    if (member.scope_type === 'district') {
      const d = orgDistricts.find((x) => x.id === member.scope_id);
      return d ? `District · ${d.name}` : 'District';
    }
    return member.scope_type;
  })();

  return (
    <li className="flex items-center gap-2 text-sm">
      <Users className="h-3 w-3 text-muted-foreground shrink-0" />
      <span className="font-medium">{member.display_name || member.email || member.user_id}</span>
      <Badge variant="secondary" className="text-[10px]">
        {ROLE_LABEL[member.role] ?? member.role}
      </Badge>
      <span className="text-xs text-muted-foreground">{scopeLabel}</span>
      {canAdmin && (
        <Button
          size="sm"
          variant="ghost"
          className="ml-auto h-6 px-2 text-[10px]"
          onClick={() => removeMemberFromScope(member)}
          disabled={mutationPending}
          title="Remove this person's membership (soft delete — preserves audit history)"
          data-testid="floo-remove-member-button"
        >
          <UserMinus className="h-3 w-3" />
        </Button>
      )}
    </li>
  );
}

// ── Phase 7 — drill-down link buttons ───────────────────────────────────

function RegionOpenButton({ regionId, className }: { regionId: string; className?: string }) {
  const navigate = useNavigate();
  return (
    <Button
      size="sm"
      variant="ghost"
      className={cn('h-7 px-2 gap-1 text-xs', className)}
      onClick={() => navigate(`/org/regions/${regionId}`)}
      title="Open Region Dashboard"
      data-testid={`floo-open-region-${regionId}`}
    >
      <ExternalLink className="h-3 w-3" />
      Open
    </Button>
  );
}

function DistrictOpenButton({ districtId, className }: { districtId: string; className?: string }) {
  const navigate = useNavigate();
  return (
    <Button
      size="sm"
      variant="ghost"
      className={cn('h-7 px-2 gap-1 text-xs', className)}
      onClick={() => navigate(`/org/districts/${districtId}`)}
      title="Open District Dashboard"
      data-testid={`floo-open-district-${districtId}`}
    >
      <ExternalLink className="h-3 w-3" />
      Open
    </Button>
  );
}

// ── Empty states ────────────────────────────────────────────────────────

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <Card>
      <CardContent className="py-10 text-center space-y-2">
        <Network className="h-8 w-8 mx-auto text-muted-foreground" />
        <p className="font-medium">{title}</p>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">{body}</p>
      </CardContent>
    </Card>
  );
}

function InlineEmpty({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs text-muted-foreground italic pl-1">{children}</p>
  );
}

export default FlooNetworkMap;
